import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Card, Stack, TextField, MenuItem,
  Button, Alert, Autocomplete, Checkbox, Chip,
  RadioGroup, Radio, FormControlLabel, FormControl, FormLabel,
} from '@mui/material'
import { Send, CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material'
import { useSendNotification, SendNotificationPayload } from '../../hooks/useNotifications'
import { useUsers, User } from '../../hooks/useUsers'

const SEVERITIES = ['info', 'warning', 'critical'] as const

export default function SendNotificationPage() {
  const { t } = useTranslation()
  const { data: users = [] } = useUsers()
  const sendMutation = useSendNotification()

  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [severity, setSeverity] = useState<'info' | 'warning' | 'critical'>('info')
  const [target, setTarget] = useState<'all' | 'specific'>('all')
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const [result, setResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const activeUsers = users.filter((u) => u.status === 'active')

  const handleSend = async () => {
    setResult(null)
    const payload: SendNotificationPayload = {
      title,
      message,
      severity,
      target,
    }
    if (target === 'specific') {
      payload.user_ids = selectedUsers.map((u) => u.id)
    }
    try {
      const res = await sendMutation.mutateAsync(payload)
      analytics.track(EVENTS.NOTIFICATION_SENT, {
        target,
        severity,
        recipient_count: res.recipients,
        push_sent: res.push_sent,
      })
      setResult({
        type: 'success',
        text: t('sendNotification.success', { count: res.recipients, push: res.push_sent }),
      })
      setTitle('')
      setMessage('')
      setSelectedUsers([])
    } catch {
      setResult({ type: 'error', text: t('sendNotification.error') })
    }
  }

  const canSend =
    title.trim().length > 0 &&
    message.trim().length > 0 &&
    (target === 'all' || selectedUsers.length > 0) &&
    !sendMutation.isPending

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
          {t('sendNotification.title')}
        </Typography>
        <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
          {t('sendNotification.subtitle')}
        </Typography>
      </Box>

      {result && (
        <Alert
          severity={result.type === 'success' ? 'success' : 'error'}
          onClose={() => setResult(null)}
          sx={{ mb: 2 }}
        >
          {result.text}
        </Alert>
      )}

      <Card sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <TextField
            label={t('sendNotification.notificationTitle')}
            placeholder={t('sendNotification.titlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            size="small"
          />

          <TextField
            label={t('sendNotification.message')}
            placeholder={t('sendNotification.messagePlaceholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            required
            multiline
            minRows={3}
            maxRows={8}
          />

          <TextField
            select
            label={t('sendNotification.severity')}
            value={severity}
            onChange={(e) => setSeverity(e.target.value as typeof severity)}
            size="small"
            sx={{ maxWidth: 220 }}
          >
            {SEVERITIES.map((s) => (
              <MenuItem key={s} value={s}>
                <Chip
                  label={t(`sendNotification.${s}`)}
                  size="small"
                  sx={{
                    bgcolor:
                      s === 'critical' ? '#fef2f2' : s === 'warning' ? '#fffbeb' : '#eff6ff',
                    color:
                      s === 'critical' ? '#ef4444' : s === 'warning' ? '#f59e0b' : '#3b82f6',
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              </MenuItem>
            ))}
          </TextField>

          <FormControl>
            <FormLabel sx={{ fontWeight: 600, fontSize: 14, color: '#475569', mb: 0.5 }}>
              {t('sendNotification.target')}
            </FormLabel>
            <RadioGroup
              row
              value={target}
              onChange={(e) => {
                setTarget(e.target.value as 'all' | 'specific')
                setSelectedUsers([])
              }}
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {t('sendNotification.allUsers')}
                    <Chip label={activeUsers.length} size="small" sx={{ fontSize: 11, height: 20 }} />
                  </Box>
                }
              />
              <FormControlLabel
                value="specific"
                control={<Radio size="small" />}
                label={t('sendNotification.specificUsers')}
              />
            </RadioGroup>
          </FormControl>

          {target === 'specific' && (
            <Autocomplete
              multiple
              size="small"
              options={activeUsers}
              getOptionLabel={(u) => `${u.name} (${u.email})`}
              value={selectedUsers}
              onChange={(_, v) => setSelectedUsers(v)}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBox fontSize="small" />}
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                    <Typography variant="caption" color="#94a3b8">{option.email}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('sendNotification.selectUsers')}
                  placeholder={t('sendNotification.selectUsers')}
                />
              )}
            />
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={handleSend}
              disabled={!canSend}
              sx={{
                background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)',
                borderRadius: 1.5,
                fontWeight: 600,
                px: 4,
              }}
            >
              {sendMutation.isPending
                ? t('sendNotification.sending')
                : t('sendNotification.send')}
            </Button>
          </Box>
        </Stack>
      </Card>
    </Box>
  )
}
